// Cookie banner. Essential / Preference / Analytics with persistence.
// Per Privacy Policy spec §9 Cookies.
// iter181.1 - Redesigned in Warm design system (cream + coral) for
// consistency with Landing / Auth / Pricing / Conditions. Same functional
// controls: Essential only / Analytics toggle / Save / Accept all.
import { useEffect, useState } from "react";
import { Cookie, ShieldCheck, X } from "lucide-react";

const KEY = "sl-cookie-prefs-v1";

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, preferences: true });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) setOpen(true);
      else setPrefs(JSON.parse(raw));
    } catch { setOpen(true); }
  }, []);

  const save = (next) => {
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* noop */ }
    setPrefs(next); setOpen(false);
  };
  const acceptAll = () => save({ analytics: true, preferences: true });
  const rejectNonEssential = () => save({ analytics: false, preferences: false });

  if (!open) return null;
  return (
    <div
      role="region"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed bottom-4 inset-x-4 sm:inset-x-6 z-[9998] rounded-3xl shadow-[0_25px_60px_-25px_rgba(27,31,59,0.35)]"
      style={{ background: "#FDF6EC", border: "2px solid #F2D9C6" }}
    >
      <div className="max-w-5xl mx-auto px-5 py-4 sm:px-6 sm:py-5 flex flex-col md:flex-row gap-4 md:items-center">
        <div className="flex items-start gap-3 flex-1">
          <div className="hidden sm:flex h-11 w-11 shrink-0 rounded-2xl items-center justify-center" style={{ background: "#FFE7D6" }}>
            <Cookie className="h-5 w-5" style={{ color: "#E5533E" }} aria-hidden="true" />
          </div>
          <div>
            <div className="text-[13px] font-black uppercase tracking-widest" style={{ color: "#E5533E" }}>Cookie preferences</div>
            <p className="mt-1 text-[13px] leading-relaxed" style={{ color: "#1B1F3B" }}>
              We use essential cookies to run SpectraLearn. With your permission we also use{" "}
              <b>preference cookies</b> (to remember your settings) and{" "}
              <b>analytics cookies</b> (PostHog, anonymised). We do not use advertising cookies.{" "}
              <a href="/privacy" className="underline font-bold" style={{ color: "#E5533E" }}>Privacy Policy</a>.
            </p>
            <label className="inline-flex items-center gap-2 text-[12px] mt-2" style={{ color: "#5C635E" }}>
              <input
                type="checkbox"
                checked={prefs.analytics}
                onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                data-testid="cookie-analytics-toggle"
                className="h-4 w-4 accent-[#E5533E]"
              />
              Allow analytics cookies (PostHog, anonymised)
            </label>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap md:flex-nowrap md:shrink-0">
          <button
            onClick={rejectNonEssential}
            data-testid="cookie-reject"
            className="h-11 px-4 rounded-full text-[13px] font-bold border-2 bg-white hover:bg-[#FFF8F2]"
            style={{ borderColor: "#F2D9C6", color: "#1B1F3B" }}
          >
            Essential only
          </button>
          <button
            onClick={() => save(prefs)}
            data-testid="cookie-save"
            className="h-11 px-4 rounded-full text-[13px] font-bold border-2 hover:bg-[#FFF8F2]"
            style={{ borderColor: "#F0805C", color: "#E5533E", background: "#FFFFFF" }}
          >
            Save my choice
          </button>
          <button
            onClick={acceptAll}
            data-testid="cookie-accept-all"
            className="h-11 px-5 rounded-full text-[13px] font-black text-white shadow-md hover:-translate-y-0.5 transition inline-flex items-center gap-1.5"
            style={{ background: "linear-gradient(180deg, #F97066 0%, #E5533E 100%)" }}
          >
            <ShieldCheck className="h-4 w-4" /> Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
